import { SUPPORTED_LANGS } from '@/hooks/useLang'

export const SITE_NAME = 'PhoneSalad'
export const SITE_URL = 'https://phonesalad.com'

export interface SeoProps {
  title?: string
  description?: string
  keywords?: string
  pathname: string
  type?: 'website' | 'article'
  ogImage?: string
}

export function generateSeo(props: SeoProps) {
  const {
    title = 'PhoneSalad — Cheap International Calls',
    description = 'Make affordable international calls to 200+ countries with crystal-clear quality. No contracts, no hidden fees — just low per-minute rates.',
    keywords,
    pathname,
    type = 'website',
    ogImage,
  } = props

  const normalizedPath = pathname === '/' ? '' : pathname.replace(/\/+$/, '')
  const canonicalUrl = `${SITE_URL}${normalizedPath}`
  const pathParts = pathname.split('/')
  const currentLang = pathParts[1] && SUPPORTED_LANGS.includes(pathParts[1] as typeof SUPPORTED_LANGS[number])
    ? pathParts[1] as typeof SUPPORTED_LANGS[number]
    : null

  const meta: any[] = [
    { title },
    { name: 'description', content: description },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:type', content: type },
    { property: 'og:url', content: canonicalUrl },
    { property: 'og:site_name', content: SITE_NAME },
    { property: 'og:image', content: ogImage || `${SITE_URL}/og.png` },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'twitter:image', content: ogImage || `${SITE_URL}/og.png` },
  ]

  if (keywords) {
    meta.push({ name: 'keywords', content: keywords })
  }

  const ogLocaleMap: Record<string, string> = {
    en: 'en_US',
    es: 'es_ES',
    pt: 'pt_BR',
    zh: 'zh_CN',
    hi: 'hi_IN',
    fil: 'fil_PH',
    fr: 'fr_FR',
  }

  if (currentLang) {
    meta.push({ property: 'og:locale', content: ogLocaleMap[currentLang] ?? 'en_US' })
  }

  const links: any[] = [
    { rel: 'canonical', href: canonicalUrl },
  ]

  if (currentLang) {
    SUPPORTED_LANGS.forEach(lang => {
      links.push({
        rel: 'alternate',
        hrefLang: lang,
        href: `${SITE_URL}${normalizedPath.replace(`/${currentLang}`, `/${lang}`)}`,
      })
    })
    links.push({
      rel: 'alternate',
      hrefLang: 'x-default',
      href: `${SITE_URL}${normalizedPath.replace(`/${currentLang}`, '/en')}`,
    })
  }

  return { meta, links }
}
